import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { ChildPowerChapter1 } from '@/components/stories/child-power/chapter-1';
import { ChildPowerChapter2 } from '@/components/stories/child-power/chapter-2';
import { ChildPowerChapter3 } from '@/components/stories/child-power/chapter-3';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/child-power.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { select, t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as ChildPowerData }),
  getStoryProps('child-power', 'free-to-be-young')
);

export default function ChildPower(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('child-power');
  const pageTitle = t({ id: 'child-power.title', message: 'Child power' });
  const pageDescription = t({
    id: 'child-power.description',
    message:
      'Most children lack the right to vote. But they can make their voices heard in other ways. Who is listening?',
  });

  const answers = [
    { id: 'very', label: t({ id: 'child-power.question.answer-1', message: 'Very important' }) },
    {
      id: 'somewhat',
      label: t({ id: 'child-power.question.answer-2', message: 'Somewhat important' }),
    },
    {
      id: 'not',
      label: t({ id: 'child-power.question.answer-3', message: 'Not at all important' }),
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  return (
    <>
      <Theme theme={Themes.sunny} hasBackButton>
        <SeoHead
          baseUrl={baseUrl}
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          sharingImage={sharingImage}
        />

        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="child-power.theme">Agency</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="child-power.tagline">
              <Text type="heading">
                Most children lack the right to vote. But they can make their voices heard in other
                ways. <span>Who is listening?</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <StoryQuestion
          question={t({
            id: 'child-power.question.question',
            message:
              'How important do you think it is for politicians to listen to children’s voices when making decisions?',
          })}
          answers={answers}
          onAnswer={handleAnswer}
          selectedAnswer={selectedAnswer}
          response={
            selectedAnswer?.id &&
            t({
              id: `child-power.question.response`,
              message: select(selectedAnswer.id, {
                very: "You're not alone!",
                somewhat: 'Others would beg to differ!',
                not: 'Others would beg to differ!',
              }),
            })
          }
        />

        <ChildPowerChapter1 data={data.importance_to_listen_to_children} />
        <ChildPowerChapter2
          dataChildPopulation={data.child_population}
          dataListenToChildren={data.importance_to_listen_to_children_income_bd}
        />
        <ChildPowerChapter3 data={data.importance_to_listen_to_children_country_bd} />

        <Outro
          css={{
            marginBlockStart: '15vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          sharing={{
            title: pageTitle,
            text: t({
              id: 'child-power.sharing.text',
              message:
                'Most children lack the right to vote. But they can make their voices heard in other ways. Who is listening?',
            }),
            url: sharingUrl,
          }}
          relatedStoryId={relatedStoryId}
        >
          <Trans id="child-power.outro-text">
            How can we encourage politicians to <AnnotatedType>pay more attention</AnnotatedType> to
            the voices of young people?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
